import axios from 'axios';
import {
  FETCH_LIST, COMPANY_INFO, ALL_COMPANIES_LIST, COMPANY_WALL_POSTS,
} from './types';
import api from '../../../utils/api';

// In-flight deduplication: concurrent callers share one Promise instead of
// each making a separate HTTP request.
// Completed cache: sequential callers skip re-fetching already-loaded data.
// Keys in use:
//   'company:{id}'           — fetchCompany(companyId)
//   'paymentEntities'        — fetchPaymentEntities()
//   'searchCompanies:{name}:{orderBy}' — searchOnCompaniesList({ name, orderBy })
//   'searchCompanyNetworkUser:{...}' — searchCompanyNetworkUser(query)
const _pendingActions = {};
const _completedActions = {};

export default {
  async fetchList({ commit }, email) {
    const response = await axios(api.companies.fetch(email));
    commit(FETCH_LIST, { companies: response.data.results });
  },
  async fetchCompanyInfo(_, companyUrl) {
    const res = await axios(api.companies.fetchInfo(companyUrl));
    return res.data;
  },
  async fetchCompany(_, companyId) {
    const key = `company:${companyId}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const res = await axios(api.companies.fetchCompany(companyId));
        _completedActions[key] = res.data;
        return res.data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async editCompanyInfo({ commit }, editData) {
    const { data } = await axios(api.companies.editCompanyInfo(editData));
    commit(COMPANY_INFO, { companyInfo: data });
  },
  async fetchAllCompanies({ commit }, { orderBy = null } = {}) {
    const { data } = await axios(api.companies.fetchAllCompanies(orderBy));
    commit(ALL_COMPANIES_LIST, {
      allCompaniesList: data.results,
      companiesCount: data.count,
      companiesNextPage: !!data.next,
    });
  },
  async searchCompanyByName(_, searchData) {
    const { data } = await axios(api.companies.searchCompanyByName(searchData));
    return data;
  },
  async fetchCompanyPublicInfo({}, id) {
    const { data } = await axios(api.companies.fetchCompanyPublicInfo(id));
    return data;
  },
  async searchCompanyNetworkUser(_, searchInfo) {
    const key = `searchCompanyNetworkUser:${searchInfo.companyId}:${searchInfo.memberName || ''}:${searchInfo.companyPage || ''}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(
          api.companies.searchCompanyNetworkUser(searchInfo),
        );
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async followUnfollowCompany({}, followdata) {
    const { data } = await axios(
      api.companies.followUnfollowCompany(followdata),
    );
    return data;
  },
  async fetchCompaniesList(_, { page = 1, orderBy = null, search = null } = {}) {
    const { data } = await axios(api.companies.fetchCompaniesList(page, orderBy, search));
    return data;
  },
  async registerCompany(_, companyData) {
    const response = await axios(api.companies.registerCompany(companyData));
    return response;
  },
  async searchOnCompaniesList(_, { name, orderBy = null } = {}) {
    // orderBy is part of the key: without it a re-search under a new sort would
    // be handed the previous ordering's in-flight promise.
    const key = `searchCompanies:${name}:${orderBy || ''}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.companies.searchOnCompaniesList(name, orderBy));
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async removeEmployee(_, employeeData) {
    const { data } = await axios(api.companies.removeEmployee(employeeData));
    return data;
  },
  async changeCompany(_, companyData) {
    const { data } = await axios(api.companies.changeCompany(companyData));
    return data;
  },
  async addRemoveAdmin(_, adminData) {
    const { data } = await axios(api.companies.addRemoveAdmin(adminData));
    return data;
  },
  async addBillingAddress(_, addressData) {
    const { data } = await axios(api.companies.addBillingAddress(addressData));
    return data;
  },
  async updateBillingAddress(_, updateData) {
    const { data } = await axios(api.companies.updateBillingAddress(updateData));
    return data;
  },
  async deleteBillingAddress(_, billingId) {
    const { data } = await axios(api.companies.deleteBillingAddress(billingId));
    return data;
  },
  async fetchPaymentEntities() {
    if (_completedActions.paymentEntities) return _completedActions.paymentEntities;
    if (_pendingActions.paymentEntities) return _pendingActions.paymentEntities;
    _pendingActions.paymentEntities = (async () => {
      try {
        const { data } = await axios(api.companies.fetchPaymentEntities());
        _completedActions.paymentEntities = data;
        return data;
      } finally {
        delete _pendingActions.paymentEntities;
      }
    })();
    return _pendingActions.paymentEntities;
  },
  async fetchAdjustmentsWithId({}, invoiceId) {
    const { data } = await axios(api.companies.fetchAdjustmentsWithId(invoiceId));
    return data.results;
  },
  async fetchBillingAddress({}, id) {
    const { data } = await axios(api.companies.fetchBillingAddress(id));
    return data;
  },
  async companyWall({ commit }, wallQuery) {
    const { data } = await axios(api.companies.companyWall(wallQuery));
    commit(COMPANY_WALL_POSTS, { companyWall: data.results, page: wallQuery.page });
    return data;
  },
};
